import { WATER_IMPULSE_SCALE } from "../../engine/core/constants";
import type { GameContext } from "../../engine/core/GameContext";
import { EventKind } from "../../engine/events/EventQueue";
import type { BondState } from "../bonds/BondState";
import type { PressureState } from "./PressureState";

/**
 * 压力模型（[决策 0016](../../../../../docs/决策/0013-核心循环改为压力爆浆.md)）。
 *
 * 体积即压力：灌进来的水累积在静止体积之上，漏水把它拉回去，被挤住则关闭漏水。
 * 半径是体积的函数，因此「胀大」既是可见的，也直接改变空间占用。
 *
 * **挤压不加压，挤压只关闭漏水。**若挤压能加压，堆积会自爆，游戏将失去失败条件。
 *
 * 状态放本机制自带的预分配表，不进 `SlimeStore`——按分层判据，下一次玩法推翻时体积会
 * 被删掉，而槽位、位置、半径不会。半径是唯一的例外：它被物理与碰撞消费，因此每步写回 Store。
 */
export class PressureSystem {
  constructor(
    private readonly pressure: PressureState,
    private readonly bonds: BondState,
  ) {}

  update(context: GameContext, dt: number): void {
    const { slimes, drops, catalog, events, level, state } = context;
    const world = level.world;
    // 每滴携带的体积由总吞吐量与射速共同决定：总量恒为 waterRate，射速只改颗粒度。
    // 曾经用一个独立的 dropRate 字段推算，它与 fireInterval 描述同一件事却悄悄对不上——
    // 实际灌注量只有设计值的两成，体积在静止值附近震荡而永远到不了临界。
    const perDrop = world.waterRate * world.fireInterval;
    const volume = this.pressure.volume;

    this.pressure.syncSlots(context);

    // 灌注：命中即体积转移。冲力沿水滴速度方向，向上分量额外乘托举系数——
    // 「水流托住它」是整形的主要手段，横向推挤只剩约 6.5% 分量。
    for (let index = 0; index < events.count; index += 1) {
      if (events.kind[index] !== EventKind.CollisionDetected) continue;
      const target = events.target[index];
      if (target < 0 || slimes.active[target] === 0 || slimes.isDying(target))
        continue;
      // 红线以下惰性：不吸水也不受冲力，打它是浪费水——它已经计过漏网，正在落出画面。
      if (slimes.y[target] > world.leakLine) continue;
      const definition = catalog.slimes[slimes.definition[target]];
      // 吸水率：0 为防水，水只推不灌。
      volume[target] += perDrop * definition.absorb;
      const source = events.source[index];
      if (source < 0 || drops.active[source] === 0) continue;
      const vx = drops.vx[source];
      const vy = drops.vy[source];
      const inverse = 1 / Math.max(1, Math.hypot(vx, vy));
      // 托举除以质量：重砣托不动，气泡一推就走。
      const push =
        (world.waterImpulse * WATER_IMPULSE_SCALE * inverse) / definition.mass;
      slimes.vx[target] += vx * push;
      slimes.vy[target] += vy * push * (vy < 0 ? world.liftFactor : 1);
    }

    // 漏水与保压，并由体积导出半径。保压有两条途径：被挤住（接触深度）与粘在团里（黏连数）。
    for (let id = 0; id < slimes.capacity; id += 1) {
      if (slimes.active[id] === 0) continue;
      const definition = catalog.slimes[slimes.definition[id]];
      const base = definition.burstVolume * world.baseFraction;
      const capacity = Math.max(1, definition.burstVolume - base);
      // 临界：到达爆裂体积即待爆，并记下步序供环解析锚定窗口。
      if (
        this.pressure.pending[id] === 0 &&
        volume[id] >= definition.burstVolume
      ) {
        this.pressure.pending[id] = 1;
        this.pressure.pendingAt[id] = state.steps;
      }
      // 待爆者绷在极限不漏水：同环成员常差 1–3 帧才到临界，先到的若边等边漏就会跌回
      // 临界以下，环随之散掉，放大机制形同虚设。
      // 红线以下一律不保压。已待爆的照常爆——它的引信是在空中点着的。
      const seal =
        this.pressure.pending[id] !== 0
          ? 1
          : slimes.y[id] > world.leakLine
            ? 0
            : Math.min(
                1,
                slimes.contactDepth[id] / world.compression +
                  this.bonds.degree[id] * world.bondSeal,
              );
      volume[id] = Math.max(
        base,
        volume[id] - world.leakRate * (1 - seal) * dt,
      );
      const fill = Math.max(0, Math.min(1.35, (volume[id] - base) / capacity));
      slimes.radius[id] =
        definition.radiusRest +
        (definition.radiusFull - definition.radiusRest) * fill;
    }
  }
}
